import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import { useDeleteLayout, useLayout, useUpdateLayout } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Pencil, Save, Trash2, X } from 'lucide-react'
import { TemplatePreview } from 'pages/templates/template-preview'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import type { LayoutDetail as LayoutDetailType } from 'types/layouts'

import { type LayoutFormData, LayoutFormFields } from './layout-form-fields'
const CONTENT_PLACEHOLDER =
  '<div style="background:#f0f0f0;padding:16px;border:1px dashed #999;text-align:center;color:#666">[Template content will appear here]</div>'

function hasContentTag(body: string): boolean {
  return /\{\{\s*content\s*\}\}/.test(body)
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value || '-'}</dd>
    </div>
  )
}

function LayoutDetailContent({ layout, refetch }: { layout: LayoutDetailType | null; refetch: () => void }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { perform: updateLayout, loading: saving } = useUpdateLayout()
  const { perform: deleteLayout } = useDeleteLayout()
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState<LayoutFormData>({ name: '', slug: '', body: '' })

  useEffect(() => {
    if (layout) {
      setFormData({ name: layout.name, slug: layout.slug, body: layout.body })
    }
  }, [layout])

  const handleFieldChange = useCallback((field: keyof LayoutFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const startEditing = useCallback(() => {
    if (layout) {
      setFormData({ name: layout.name, slug: layout.slug, body: layout.body })
    }
    setEditing(true)
  }, [layout])

  const cancelEditing = useCallback(() => {
    if (layout) {
      setFormData({ name: layout.name, slug: layout.slug, body: layout.body })
    }
    setEditing(false)
  }, [layout])

  const contentValid = hasContentTag(formData.body)

  const handleSave = useCallback(async () => {
    if (!id) {
      return
    }
    try {
      await updateLayout(id, {
        name: formData.name,
        body: formData.body,
      })
      refetch()
      setEditing(false)
    } catch (err) {
      toast.error('Failed to save layout', { description: getApiErrorMessage(err) })
    }
  }, [id, formData, updateLayout, refetch])

  const handleDelete = useCallback(async () => {
    if (!id) {
      return
    }
    try {
      await deleteLayout(id)
      navigate('/layouts')
    } catch (err) {
      toast.error('Failed to delete layout', { description: getApiErrorMessage(err) })
    }
  }, [id, deleteLayout, navigate])

  if (!layout) {
    return <div className="py-12 text-center text-muted-foreground">Layout not found</div>
  }

  const currentBody = editing ? formData.body : layout.body
  const previewBody = currentBody.replace(/\{\{\s*content\s*\}\}/g, CONTENT_PLACEHOLDER)

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <Card className="min-w-0 flex-1">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle>{layout.name}</CardTitle>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                  <X className="mr-1 h-4 w-4" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving || !contentValid}>
                  <Save className="mr-1 h-4 w-4" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Pencil className="mr-1 h-4 w-4" />
                  Edit
                </Button>
                <ConfirmDialog
                  title="Delete layout"
                  description={`Are you sure you want to delete "${layout.name}"? Templates using this layout will no longer have one.`}
                  onConfirm={handleDelete}
                  trigger={
                    <Button variant="destructive" size="sm">
                      <Trash2 className="mr-1 h-4 w-4" />
                      Delete
                    </Button>
                  }
                />
              </>
            )}
          </div>
        </CardHeader>

        <Separator />

        <CardContent className="pt-6">
          {editing ? (
            <>
              <LayoutFormFields data={formData} onChange={handleFieldChange} slugLocked />
              {formData.body.trim() && !contentValid && (
                <p className="mt-2 text-sm text-destructive">Layout must contain a {'{{ content }}'} variable.</p>
              )}
            </>
          ) : (
            <div className="flex flex-col gap-4">
              <InfoRow label="Name" value={layout.name} />
              <div className="space-y-0.5">
                <dt className="text-sm font-medium text-muted-foreground">Layout Body</dt>
                <dd className="break-all whitespace-pre-wrap rounded-md bg-muted/30 px-3 py-2 font-mono text-sm">
                  {layout.body || '-'}
                </dd>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <TemplatePreview body={previewBody} mode="html" />
      </div>
    </div>
  )
}

export function LayoutDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: layout, loading, refetch } = useLayout(id)

  if (!loading && !layout) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">Layout not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/layouts">Back to Layouts</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        }>
        <LayoutDetailContent layout={layout} refetch={refetch} />
      </LoadingWrapper>
    </Page>
  )
}
